import { FadeUp, ScaleOnScroll } from "../components/Animations";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

const LegacyVisionPage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);

  return (
    <div data-testid="legacy-vision-page">
      {/* Hero */}
      <section 
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <motion.div
          style={{ scale: heroScale }}
          className="absolute inset-0"
        >
          <img
            src="https://images.unsplash.com/photo-1525623434043-13387b9c56e2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbmNyZXRlJTIwYXJjaGl0ZWN0dXJlJTIwbGlnaHQlMjBzaGFkb3d8ZW58MHx8fHwxNzcwNzY2OTYwfDA&ixlib=rb-4.1.0&q=85"
            alt="Architectural abstract"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-champagne/60" />
        </motion.div>

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="relative z-10 container-wide text-center"
        >
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="text-caption text-bronze mb-6 block">Legacy & Vision</span>
            <h1 className="text-display text-charcoal max-w-4xl mx-auto">
              Building what endures.
            </h1>
          </motion.div>
        </motion.div>
      </section>

      {/* Long-term Value Creation */}
      <section className="section-spacing">
        <div className="container-wide">
          <div className="max-w-4xl">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Vision</span>
              <h2 className="text-headline text-charcoal mb-12">
                Long-term value creation.
              </h2>
            </FadeUp>

            <div className="space-y-8">
              <FadeUp delay={0.1}>
                <p className="text-xl md:text-2xl font-serif text-charcoal leading-relaxed">
                  We measure success not in quarters, but in decades. Our investments 
                  are structured to compound value across generations, creating lasting 
                  impact for stakeholders and communities.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body text-lg">
                  This long-term orientation shapes every aspect of our approach—from 
                  the assets we acquire to the teams we build to the relationships we cultivate.
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Geographic Permanence */}
      <section className="section-spacing bg-white grain">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <FadeUp>
              <div className="aspect-square relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1770546597346-adb6f350812c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxzYW5kJTIwZHVuZXMlMjBtaW5pbWFsJTIwYmVpZ2V8ZW58MHx8fHwxNzcwNzY2OTY4fDA&ixlib=rb-4.1.0&q=85"
                  alt="Desert landscape"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-bronze/5" />
              </div>
            </FadeUp>

            <div>
              <FadeUp>
                <span className="text-caption text-bronze mb-6 block">Presence</span>
                <h2 className="text-headline text-charcoal mb-8">
                  Geographic permanence.
                </h2>
              </FadeUp>

              <FadeUp delay={0.1}>
                <p className="text-body text-lg mb-6">
                  We establish deep roots in the regions where we operate. Our presence 
                  is not opportunistic but foundational—built on relationships, understanding, 
                  and commitment that span cycles.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body">
                  From Dubai to London to Lahore, we invest in communities as much as 
                  we invest in assets. This permanence creates trust, attracts talent, 
                  and enables the patient approach that defines our strategy.
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Inter-generational Capital */}
      <section className="section-spacing">
        <div className="container-wide">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <FadeUp>
                <span className="text-caption text-bronze mb-6 block">Capital</span>
                <h2 className="text-headline text-charcoal">
                  Inter-generational thinking.
                </h2>
              </FadeUp>
            </div>

            <div className="lg:col-span-6 lg:col-start-7">
              <FadeUp delay={0.1}>
                <p className="text-body text-lg mb-8">
                  Our capital structure is designed for permanence. Without the constraints 
                  of fund cycles or investor redemptions, we can truly invest with the 
                  long-term horizon that exceptional returns require.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <p className="text-body mb-8">
                  This enables us to make decisions that may not optimize short-term 
                  performance but create substantial value over time—investing in people, 
                  building infrastructure, and waiting for the right opportunities.
                </p>
              </FadeUp>

              <FadeUp delay={0.3}>
                <p className="text-body">
                  We think in terms of legacy, not liquidity events. The wealth we 
                  steward is meant to compound across generations, creating impact 
                  that extends far beyond any individual investment horizon.
                </p>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* Building Systems */}
      <section className="section-spacing bg-charcoal">
        <div className="container-wide">
          <div className="max-w-4xl mx-auto text-center">
            <FadeUp>
              <span className="text-caption text-bronze mb-6 block">Philosophy</span>
              <h2 className="text-headline text-champagne mb-12">
                Building systems, not trends.
              </h2>
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-xl md:text-2xl font-serif text-champagne/80 leading-relaxed mb-8">
                We resist the temptation to chase what is fashionable. Instead, we 
                focus on fundamental value creation through disciplined processes 
                and institutional rigor.
              </p>
            </FadeUp>

            <FadeUp delay={0.2}>
              <p className="text-body text-champagne/60">
                Trends come and go. Systems endure. Our approach is designed to 
                perform through cycles, creating value regardless of market sentiment 
                or popular narratives.
              </p>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* Final Statement */}
      <section className="relative h-screen flex items-center justify-center">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1682368724572-61e83de6c91f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxncmVlbiUyMGFncmljdWx0dXJlJTIwZmllbGQlMjBhZXJpYWx8ZW58MHx8fHwxNzcwNzY2OTgwfDA&ixlib=rb-4.1.0&q=85"
            alt="Aerial landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-charcoal/70" />
        </div>

        <div className="relative z-10 container-wide text-center">
          <ScaleOnScroll>
            <p className="text-3xl md:text-4xl lg:text-5xl font-serif text-champagne max-w-4xl mx-auto leading-[1.3]">
              Capital flows. Value compounds. Legacy remains.
            </p>
          </ScaleOnScroll>
        </div>
      </section>
    </div>
  );
};

export default LegacyVisionPage;
